<template>
  <v-container class="constructions pb-16">
    <v-btn
      v-if="isAdmin"
      color="primary"
      dark
      elevation="2"
      fab
      fixed
      bottom
      right
      :disabled="isBusy"
      @click="openForm()">
      <v-icon>mdi-plus</v-icon>
    </v-btn>
    <v-row>
      <v-col cols="12" class="mt-3 mb-3">
        <h2 class="text-h4">Конструкції</h2>
      </v-col>
      <v-col v-if="loadError" cols="12">
        <v-alert type="error" class="mb-0">
          {{ loadError }}
          <v-btn text small :disabled="isBusy" @click="loadConstructionTypes">
            Спробувати ще раз
          </v-btn>
        </v-alert>
      </v-col>
      <v-col cols="12">
        <v-sheet outlined rounded>
          <v-data-table
            :headers="tableHeaders"
            :items="constructionTypes"
            :loading="isPageLoading"
            hide-default-footer
            disable-filtering
            :items-per-page="-1"
            no-data-text="На жаль, немає жодного типу конструкції.">
            <template v-slot:item.name="{ item }">
              {{ item.name }}
              <v-chip v-if="item.is_default" x-small class="ml-1">
                За замовчуванням
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <template v-if="isAdmin && !item.is_default">
                <v-btn
                  small
                  icon
                  class="mr-2"
                  title="Перейменувати"
                  :disabled="isBusy"
                  @click="openForm(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  small
                  icon
                  title="Видалити"
                  :disabled="isBusy"
                  @click="confirmDelete(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <AppPageLoader :visible="isPageLoading" />
    <v-dialog v-model="isFormVisible" :persistent="isSaving" max-width="500px">
      <v-card :loading="isSaving">
        <v-card-title class="pt-5 pb-5">
          {{ typeToEdit ? 'Перейменувати' : 'Новий' }} тип конструкції
        </v-card-title>
        <v-card-text>
          <v-alert v-if="formError" type="error" dense>{{ formError }}</v-alert>
          <v-form @submit.prevent="saveConstructionType">
            <v-text-field
              v-model="constructionName"
              label="Назва"
              outlined
              hide-details="auto"
              autofocus
              counter="255"
              class="is-required"
              :disabled="isSaving"
              :error-messages="nameErrors"
              @input="clearFormErrors"
              @blur="$v.constructionName.$touch()" />
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" :disabled="isSaving" @click="isFormVisible = false">
            Відмінити
          </v-btn>
          <v-btn
            text
            color="primary"
            :loading="isSaving"
            :disabled="isSaving"
            @click="saveConstructionType">
            {{ typeToEdit ? 'Зберегти' : 'Додати' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteVisible" :persistent="isDeleting" max-width="500px">
      <v-card :loading="isDeleting">
        <v-card-title class="pt-5 pb-5">Видалити тип конструкції?</v-card-title>
        <v-card-text>
          <p v-if="typeToDelete" class="font-weight-bold">{{ typeToDelete.name }}</p>
          <p>
            Білборди залишаться в каталозі. Їхній тип конструкції зміниться на
            «Тип конструкції не закріплено».
          </p>
          <v-alert v-if="deleteError" type="error" dense class="mb-0">
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="grey" :disabled="isDeleting" @click="isDeleteVisible = false">
            Відмінити
          </v-btn>
          <v-btn
            text
            color="red"
            :loading="isDeleting"
            :disabled="isDeleting"
            @click="deleteConstructionType">
            Видалити
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack" :timeout="5000" color="success">
      {{ snackText }}
      <template v-slot:action="{ attrs }">
        <v-btn v-bind="attrs" icon @click="snack = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators';
import {
  GET_CONSTRUCTION_TYPES,
  ADD_CONSTRUCTION_TYPE,
  EDIT_CONSTRUCTION_TYPE,
  DELETE_CONSTRUCTION_TYPE,
} from '@/store/constructionTypes/types';

export default {
  name: 'Constructions',
  data: () => ({
    constructionName: '',
    typeToEdit: null,
    typeToDelete: null,
    isFormVisible: false,
    isDeleteVisible: false,
    isPageLoading: false,
    isSaving: false,
    isDeleting: false,
    loadError: '',
    formError: '',
    serverNameErrors: [],
    deleteError: '',
    snack: false,
    snackText: '',
    tableHeaders: [
      { text: 'Назва', value: 'name' },
      { text: 'Дія', value: 'actions', sortable: false, width: 110 },
    ],
  }),
  computed: {
    constructionTypes() {
      return this.$store.getters.constructionTypes;
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isBusy() {
      return this.isPageLoading || this.isSaving || this.isDeleting;
    },
    nameErrors() {
      const errors = [...this.serverNameErrors];
      if (!this.$v.constructionName.$dirty) return errors;
      if (!this.$v.constructionName.required) errors.push("Назва обов'язкова.");
      if (!this.$v.constructionName.maxLength) errors.push('Максимум 255 символів.');
      return errors;
    },
  },
  methods: {
    apiError(error, fallback) {
      const data = error.response && error.response.data;
      return data && typeof data.message === 'string' && data.message
        ? data.message
        : fallback;
    },
    async loadConstructionTypes() {
      if (this.isPageLoading) return;
      this.isPageLoading = true;
      this.loadError = '';
      try {
        await this.$store.dispatch(GET_CONSTRUCTION_TYPES);
      } catch (error) {
        this.loadError = this.apiError(error, 'Не вдалося завантажити типи конструкцій.');
      } finally {
        this.isPageLoading = false;
      }
    },
    clearFormErrors() {
      this.formError = '';
      this.serverNameErrors = [];
    },
    openForm(constructionType = null) {
      if (!this.isAdmin || this.isBusy || (constructionType && constructionType.is_default)) return;
      this.typeToEdit = constructionType;
      this.constructionName = constructionType ? constructionType.name : '';
      this.clearFormErrors();
      this.$v.$reset();
      this.isFormVisible = true;
    },
    async saveConstructionType() {
      if (!this.isAdmin || this.isSaving || (this.typeToEdit && this.typeToEdit.is_default)) return;
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isSaving = true;
      this.clearFormErrors();
      const isEditing = Boolean(this.typeToEdit);
      try {
        const constructionType = { name: this.constructionName.trim() };
        if (isEditing) constructionType.id = this.typeToEdit.id;
        await this.$store.dispatch(
          isEditing ? EDIT_CONSTRUCTION_TYPE : ADD_CONSTRUCTION_TYPE,
          constructionType
        );
        this.isFormVisible = false;
        this.snackText = isEditing ? 'Назву змінено.' : 'Тип конструкції додано.';
        this.snack = true;
        await this.loadConstructionTypes();
      } catch (error) {
        const response = error.response;
        const fieldErrors = response && response.status === 422 && response.data && response.data.errors;
        if (fieldErrors && fieldErrors.name) {
          this.serverNameErrors = Array.isArray(fieldErrors.name)
            ? fieldErrors.name
            : [fieldErrors.name];
        } else {
          this.formError = this.apiError(error, 'Не вдалося зберегти тип конструкції.');
        }
      } finally {
        this.isSaving = false;
      }
    },
    confirmDelete(constructionType) {
      if (!this.isAdmin || this.isBusy || constructionType.is_default) return;
      this.typeToDelete = constructionType;
      this.deleteError = '';
      this.isDeleteVisible = true;
    },
    async deleteConstructionType() {
      if (!this.isAdmin || this.isDeleting || !this.typeToDelete || this.typeToDelete.is_default) return;
      this.isDeleting = true;
      this.deleteError = '';
      try {
        await this.$store.dispatch(DELETE_CONSTRUCTION_TYPE, this.typeToDelete.id);
        this.isDeleteVisible = false;
        this.typeToDelete = null;
        this.snackText = 'Тип конструкції видалено. Білборди збережено.';
        this.snack = true;
        await this.loadConstructionTypes();
      } catch (error) {
        this.deleteError = this.apiError(error, 'Не вдалося видалити тип конструкції.');
      } finally {
        this.isDeleting = false;
      }
    },
  },
  created() {
    this.loadConstructionTypes();
  },
  validations: {
    constructionName: {
      required: (value) => required(value.trim()),
      maxLength: maxLength(255),
    },
  },
};
</script>

<style lang="scss" scoped>
.constructions {
  max-width: 700px;
}
</style>
